import { FunctionComponent, useEffect, useState } from 'react'
import styles from './exportDialog.module.scss'
import Dialog from '../_custom/Dialog/Dialog.tsx'
import DialogHeader from '../_custom/DialogHeader/DialogHeader.tsx'
import DialogBody from '../_custom/DialogBody/DialogBody.tsx'
import { downloadBase64File } from '@/modules/utils/downloader.ts'
import { useTranslation } from 'react-i18next'
import ButtonTextIcon from '../_custom/Buttons/ButtonTextIcon/ButtonTextIcon.tsx'
import { ExportDataType } from '@/types/Export.ts'
import DatePickerComponent from '../_custom/Picker/datePicker/datePicker.tsx'
import { useLazyQuery } from '@apollo/client'
import { GET_EXPORT_DATA } from '@/modules/API/export/getExportData.ts'
import { Daycare } from '@/types/Daycare.ts'
import { SelectOptionDeprecated, SelectOptionsDeprecated } from '@/types/Select.ts'
import SelectMultiple from '../_custom/Selector/selectMultiple/selectMultiple.tsx'
import moment, { Moment } from 'moment/moment'
import Checkbox from '../_custom/Checkbox/Checkbox.tsx'
import { initYearsTab, Year } from '@/container/settings/cron/initValueTab.tsx'
import SelectUnique from '../_custom/Selector/selectUnique/selectUnique.tsx'
import Loading from '../Loading/Loading.tsx'
import ErrorMessage from '../_custom/ErrorMessage/ErrorMessage.tsx'

interface ExportDialogProps {
    open: boolean
    handleClose: () => void
    minDate: string
    daycares: Daycare[]
    type: ExportDataType
    isRpeOrLaep?: boolean
    preSelectedFrom?: string
    preSelectedTo?: string
}

const ExportDialog: FunctionComponent<ExportDialogProps> = ({
    open,
    handleClose,
    minDate,
    daycares,
    type,
    isRpeOrLaep = false,
    preSelectedFrom,
    preSelectedTo
}: ExportDialogProps) => {
    const { t } = useTranslation()

    const defaultDate: Moment = moment().subtract(1, 'month')

    const [startDate, setStartDate] = useState<string>(
        preSelectedFrom ? preSelectedFrom : defaultDate.startOf('month').format('DD-MM-YYYY')
    )
    const [toDate, setToDate] = useState<string>(
        preSelectedTo ? preSelectedTo : defaultDate.endOf('month').format('DD-MM-YYYY')
    )

    const [selectPeriod, setSelectPeriod] = useState<boolean>(!!(preSelectedFrom && preSelectedTo))

    const [selectedYear, setSelectedYear] = useState<string>(defaultDate.format('YYYY'))

    const selectOptionsYear: SelectOptionsDeprecated = [
        {
            options: initYearsTab(moment(minDate, 'DD-MM-YYYY').year()).map((year: Year): SelectOptionDeprecated => {
                return {
                    name: year.years,
                    value: year.id
                }
            })
        }
    ]

    const [selectedDaycare, setSelectedDaycare] = useState<string[]>(
        daycares.map((daycare: Daycare): string => daycare.id.toString())
    )

    const daycareOptions: SelectOptionsDeprecated = [
        {
            options: daycares.map((daycare: Daycare): SelectOptionDeprecated => {
                return {
                    name: daycare.name,
                    value: daycare.id.toString()
                }
            })
        }
    ]

    const [getExportData, { data: dataExport, loading: loadingExport }] = useLazyQuery(GET_EXPORT_DATA, {
        variables: {
            daycareIds: selectedDaycare.map((id: string): number => Number(id)),
            from: selectPeriod ? startDate : '01-01-' + selectedYear,
            to: selectPeriod ? toDate : '31-12-' + selectedYear,
            type: type
        }
    })

    useEffect(() => {
        if (dataExport && dataExport.getExportData) {
            downloadBase64File(
                dataExport.getExportData.base64Data,
                `${getFileName(t, type, selectPeriod, startDate, toDate, selectedYear, isRpeOrLaep).trim()}.xlsx`,
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            ).then(() => {
                handleClose()
            })
        }
    }, [dataExport])

    useEffect(() => {
        setSelectedDaycare(daycares.map((daycare: Daycare): string => daycare.id.toString()))
    }, [daycares])

    const isRangeTooLong: boolean =
        startDate && toDate ? moment(toDate, 'DD-MM-YYYY').diff(moment(startDate, 'DD-MM-YYYY'), 'days') > 365 : false

    return (
        <Dialog open={open} handleClose={handleClose}>
            <DialogHeader title={t(defineDialogTitle(type, isRpeOrLaep))}></DialogHeader>
            <DialogBody>
                <div className={styles.DialogBody}>
                    {loadingExport ? (
                        <Loading overall={false} />
                    ) : (
                        <>
                            {!(type === ExportDataType.Daycares || type === ExportDataType.Trustees) && (
                                <>
                                    {daycares.length > 1 && (
                                        <div className={styles.SelectContainer}>
                                            <SelectMultiple
                                                label={t('myDaycares')}
                                                selectOptions={daycareOptions}
                                                selectValues={selectedDaycare}
                                                setSelectValues={setSelectedDaycare}
                                                className={styles.UserInput}
                                                maxToShow={5}
                                            />
                                        </div>
                                    )}
                                    {!(
                                        type === ExportDataType.Staffs ||
                                        type === ExportDataType.Parents ||
                                        type === ExportDataType.Childs
                                    ) && (
                                        <>
                                            {selectPeriod ? (
                                                <div className={styles.SelectContainer}>
                                                    <DatePickerComponent
                                                        minDate={minDate}
                                                        maxDate={toDate}
                                                        value={startDate}
                                                        setValue={setStartDate}
                                                        label={t('fromDate')}
                                                        className={styles.UserInput}
                                                        disablePast={false}
                                                    />
                                                    <DatePickerComponent
                                                        minDate={startDate}
                                                        maxDate={moment().format('DD-MM-YYYY')}
                                                        value={toDate}
                                                        setValue={setToDate}
                                                        label={t('toDate')}
                                                        className={styles.UserInput}
                                                        disablePast={false}
                                                    />
                                                </div>
                                            ) : (
                                                <div className={styles.SelectContainer}>
                                                    <SelectUnique
                                                        selectOptions={selectOptionsYear}
                                                        selectValue={selectedYear}
                                                        setSelectValue={setSelectedYear}
                                                        label={t('year')}
                                                        formClassName={styles.UserInput}
                                                    />
                                                </div>
                                            )}
                                            {isRangeTooLong && <ErrorMessage error={t('dateOverOneYear')} />}
                                            <div className={styles.CheckBoxContainer}>
                                                <Checkbox
                                                    text={t('selectPeriod')}
                                                    checked={selectPeriod}
                                                    onChange={(checked: boolean) => setSelectPeriod(checked)}
                                                    className={styles.CheckBox}
                                                />
                                            </div>
                                        </>
                                    )}
                                </>
                            )}
                        </>
                    )}
                    <div className={styles.ButtonContainer}>
                        <ButtonTextIcon type={'close'} onClick={handleClose} className={styles.Button} />
                        <ButtonTextIcon
                            type={'export'}
                            onClick={getExportData}
                            className={styles.Button}
                            disabled={loadingExport || isRangeTooLong}
                        />
                    </div>
                </div>
            </DialogBody>
        </Dialog>
    )
}

export default ExportDialog

function defineDialogTitle(type: ExportDataType, isRpeOrLaep: boolean): string {
    switch (type) {
        case ExportDataType.TaskNotes:
            return 'exportTaskNotes'
        case ExportDataType.Absences:
            return 'exportAbsences'
        case ExportDataType.Parents:
            return isRpeOrLaep ? 'exportPublicList' : 'exportParentList'
        case ExportDataType.OccasionalSlots:
            return isRpeOrLaep ? 'exportAnimationSlots' : 'exportOccasionalSlots'
        case ExportDataType.RegularSlots:
            return isRpeOrLaep ? 'exportRegularGroups' : 'exportRegularSlots'
        case ExportDataType.Staffs:
            return 'exportStaffList'
        case ExportDataType.Works:
            return 'exportWorks'
        case ExportDataType.Daycares:
            return 'exportDaycareList'
        case ExportDataType.Trustees:
            return 'exportTrusteeList'
        case ExportDataType.Childs:
            return 'exportChildList'
        case ExportDataType.Events:
            return 'exportEvents'
        case ExportDataType.Calendar:
            return 'exportCalendar'
        default:
            return ''
    }
}

function getFileName(
    t: (key: string) => string,
    type: ExportDataType,
    selectPeriod: boolean,
    startDate: string,
    toDate: string,
    selectedYear: string,
    isRpeOrLaep: boolean
): string {
    let typeString: string = ''

    switch (type) {
        case ExportDataType.TaskNotes:
            typeString = t('tasksAndNotes')
            break
        case ExportDataType.Absences:
            typeString = t('absences')
            break
        case ExportDataType.Parents:
            typeString = t(isRpeOrLaep ? 'publics' : 'parents')
            break
        case ExportDataType.OccasionalSlots:
            typeString = t(isRpeOrLaep ? 'animations' : 'occasional')
            break
        case ExportDataType.RegularSlots:
            typeString = t(isRpeOrLaep ? 'groups' : 'regular')
            break
        case ExportDataType.Staffs:
            typeString = t('staffs')
            break
        case ExportDataType.Works:
            typeString = t('staffWork')
            break
        case ExportDataType.Daycares:
            typeString = t('structures')
            break
        case ExportDataType.Trustees:
            typeString = t('trustees')
            break
        case ExportDataType.Childs:
            typeString = t('childs')
            break
        case ExportDataType.Events:
            typeString = t('events')
            break
        case ExportDataType.Calendar:
            typeString = t('calendar')
            break
        default:
            break
    }

    const date: string = selectPeriod
        ? moment(startDate, 'DD-MM-YYYY').format('YYYY-MM-DD') + '_' + moment(toDate, 'DD-MM-YYYY').format('YYYY-MM-DD')
        : selectedYear

    switch (type) {
        case ExportDataType.Staffs:
        case ExportDataType.Parents:
        case ExportDataType.Daycares:
        case ExportDataType.Trustees:
        case ExportDataType.Childs:
            return `${typeString.toLowerCase()}_${moment().format('YYYY-MM-DD')}`
        default:
            return `${typeString.toLowerCase()}_${date}`
    }
}
